import { X } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { Pressable, StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import { formatFileSize } from '@/lib/fileSize';
import type { UploadItem } from '@/stores/voltrUpload.store';

import { UploadFileTile } from './UploadFileTile';

export type UploadFileCardProps = {
  item: UploadItem;
  /** While its bytes go up: stop and drop the picks. */
  onCancel?: () => void;
  /** Before it's sent (or after it failed): the ✕ that drops it. */
  onRemove?: () => void;
  testID?: string;
};

const PHASE_COPY = {
  idle: 'Ready to submit',
  preparing: 'Getting it ready…',
  processing: 'Processing…',
  waiting: "Still processing. You don't need to upload it again.",
  done: 'Uploaded',
} as const;

/**
 * One picked video on the upload step (Figma "File name" card): tile, name,
 * size, where it stands, and a ✕ before it's sent; once it's going, a
 * progress bar and Cancel while its bytes go up. An observer: progress
 * changes many times a second.
 */
export const UploadFileCard = observer(function UploadFileCard({ item, onCancel, onRemove, testID }: UploadFileCardProps) {
  const pct = item.totalBytes > 0 ? Math.round((item.sentBytes / item.totalBytes) * 100) : 0;
  const failed = item.phase === 'error';
  const status = item.phase === 'sending' ? `Uploading ${pct}%` : item.phase === 'error' ? item.error : PHASE_COPY[item.phase];
  // Uploading fills as the parts land; with Voltr it reads full. Waiting to be sent, no bar.
  const fill = item.phase === 'sending' ? pct : item.phase === 'preparing' ? 0 : 100;
  const unsent = item.phase === 'idle' || failed;
  const size = formatFileSize(item.totalBytes || item.video.sizeBytes);
  const uploading = item.phase === 'preparing' || item.phase === 'sending';
  return (
    <View style={styles.card} testID={testID}>
      <View style={styles.row} accessible accessibilityLabel={[item.video.fileName, size, status].filter(Boolean).join(', ')}>
        <UploadFileTile />
        <View style={styles.titles}>
          <Text variant="bodyBold" color="deepGreen" numberOfLines={1}>
            {item.video.fileName}
          </Text>
          {size ? (
            <Text variant="caption" color="textMuted">
              {size}
            </Text>
          ) : null}
          <Text variant="captionBold" color={failed ? 'danger' : 'deepGreen'} testID={testID ? `${testID}-status` : undefined}>
            {status}
          </Text>
        </View>
        {unsent && onRemove ? (
          <Pressable
            onPress={onRemove}
            hitSlop={REMOVE_SLOP}
            accessibilityRole="button"
            accessibilityLabel={`Remove ${item.video.fileName}`}
            style={({ pressed }) => [styles.remove, pressed && styles.pressed]}
            testID={testID ? `${testID}-remove` : undefined}
          >
            <X size={18} color={colors.textMuted} strokeWidth={2.5} />
          </Pressable>
        ) : null}
      </View>
      {unsent ? null : (
        <View style={styles.track} testID={testID ? `${testID}-bar` : undefined}>
          <View style={[styles.fill, { width: `${fill}%` }]} />
        </View>
      )}
      {uploading && onCancel ? <Button label="Cancel" variant="outline" fullWidth onPress={onCancel} testID={testID ? `${testID}-cancel` : undefined} /> : null}
    </View>
  );
});

// An 18pt ✕ with 13pt around it reaches the 44pt touch target.
const REMOVE_SLOP = 13;

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  titles: { flex: 1, gap: spacing.xs },
  track: { height: 7, borderRadius: radii.full, overflow: 'hidden', backgroundColor: colors.track },
  fill: { height: '100%', borderRadius: radii.full, backgroundColor: colors.electricGreen },
  remove: { alignSelf: 'flex-start' },
  pressed: { opacity: 0.6 },
});
